import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Store } from "@ngrx/store";
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    catchError,
    filter,
    first,
} from "rxjs";

import * as M from "../../data/model";
import { RootState } from "../root";
import { stateBuilder } from "../utils";
import { PersistenceEntities } from "./persistence-entities";
import { UpdateCache } from "./update-cache.service";
import { NotificationService } from "../../services/notification.service";
import {
    UpdateAction,
    AddAction,
    ReplaceDatabaseAction,
    DeleteAction,
} from "./actions";
import { Attachment } from "../../data/model";
import { assert } from "../../utils/debug";
import { LoggerService } from "../../services/logger.service";

@Injectable()
export class PersistenceService extends PersistenceEntities {
    protected serviceUrl = config.ZBDSERVICEURL;

    protected state$: Observable<LocalState>;
    protected cp: M.ConsultationProcess;
    /** There must be only one delete request, else only one will succeed */
    private deleteChain: Promise<any> = Promise.resolve();
    private activeOperationCounter = 0;

    public isPending = new BehaviorSubject<boolean>(false);

    private logger = inject(LoggerService);

    constructor(
        protected http: HttpClient,
        protected updateCache: UpdateCache,
        protected store: Store<RootState>,
        private notificationService: NotificationService,
    ) {
        super(http, updateCache);
        this.state$ = LocalState.create(store);

        this.state$.subscribe((state) => {
            this.cp = state.cp;
        });

        this.updateCache.isUpdating$.subscribe(() => this.adaptPending());
    }

    protected addPending() {
        this.activeOperationCounter++;
        this.adaptPending();
    }

    protected finishedPending() {
        this.activeOperationCounter--;
        this.adaptPending();
    }

    private adaptPending() {
        const isPending =
            this.activeOperationCounter > 0 ||
            this.updateCache.isUpdating$.value;
        if (isPending && !this.isPending.value) {
            this.isPending.next(true);
        } else if (!isPending && this.isPending.value) {
            this.isPending.next(false);
        }
    }

    public load(cpId: number): void {
        const url = `${this.serviceUrl}/ConsultationProcesses(${cpId})?$expand=*($levels=20)`;
        this.http.get<M.ConsultationProcess>(url).subscribe(
            (cp) => {
                this.logger.log("Loaded CP with ID %d", cp.Id);
                this.store.dispatch(new ReplaceDatabaseAction(cp));
            },
            (error) => {
                this.notificationService.errorExt(
                    "Vorgang laden",
                    "Vorgang konnte nicht geladen werden.",
                    error,
                );
                this.logger.error(
                    "Could not load ConsultationProcess with ID %d: %s",
                    cpId,
                    error,
                );
            },
        );
    }

    public reload() {
        this.load(this.cp.Id);
    }

    protected unboundAction<T>(name: string, args: any): Observable<T> {
        const url = `${this.serviceUrl}/${name}?$expand=*($levels=20)`;
        return this.http.post<T>(url, args, this.reqOpts);
    }

    public mergeConsultationProcess(
        cpId: number,
        cp: M.ConsultationProcess,
    ): Observable<any> {
        return this.unboundAction("updateConsultationProcess", {
            cpId: cpId,
            cp: cp,
        });
    }

    protected boundAction<T>(
        name: string,
        args: any,
        query: string = undefined,
    ) {
        let url = `${this.serviceUrl}/ConsultationProcesses(${this.cp.Id})/com.zurich.zbd.data.model.ConsultationProcess.${name}`;
        if (query === undefined) {
            url += "?$expand=*($levels=20)";
        } else if (query) {
            url += "?" + query;
        }
        return this.http.post<T>(url, args, this.reqOpts);
    }

    public createDocument(
        cp: M.ConsultationProcess,
    ): Observable<M.CreateDocumentResult> {
        return this.boundAction(
            "createDocument",
            {},
            "$expand=Document,SignatureFields",
        );
    }

    public setCustomerType(isCompany: boolean, salutation: string) {
        const oldCustomer = this.cp.Customer;
        this.boundAction("setCustomerType", {
            isCompany: isCompany,
            salutation: salutation,
        }).subscribe((newCustomer) => {
            newCustomer["@path"] = oldCustomer["@path"];
            this.store.dispatch(new UpdateAction(newCustomer));
        });
    }

    public addOccasionEntries(entries: M.OccasionOccasionEntry[]) {
        this.boundAction<Record<string, M.OccasionOccasionEntry[]>>(
            "addOccasionEntries",
            {
                entries: {
                    Entries: entries,
                },
            },
        ).subscribe((newEntries) => {
            // TODO: Batch-process this
            newEntries.value.forEach((entry) => {
                this.store.dispatch(
                    new AddAction(
                        this.cp.Protocol.OccasionOccasionEntries,
                        entry,
                    ),
                );
            });
        });
    }

    public addDeepGlobalOccasionSection(section: M.GlobalOccasionSection) {
        this.boundAction("addGlobalOccasionSection", {
            section: section,
        }).subscribe((newSection) => {
            this.store.dispatch(
                new AddAction(
                    this.cp.Protocol.GlobalOccasion.GlobalOccasionSections,
                    newSection,
                ),
            );
        });
    }

    /**
     * Divisions are stored in an array. As a result, their path may change during handling. This reloads the division and ensures, that the correct index is used
     * @param division Current version of the given division
     */
    private reloadDivisionFromStore(division: M.Division) {
        return this.store
            .select((s) =>
                division["@path"]
                    .slice(0, -1)
                    .reduce((o: object, k: string | number) => o[k], s.model)
                    .find((item: M.Division) => {
                        return division.Id
                            ? item.Id === division.Id
                            : item.InstanceKey === division.InstanceKey;
                    }),
            )
            .pipe(
                filter((_) => !!_),
                first(),
            );
    }

    public addManualDivision(customOffer: M.Offer, division: M.Division) {
        this.addPending();
        if (customOffer) {
            this.store.dispatch(new AddAction(customOffer.Divisions, division));
            this.boundAction("addManualDivision", {
                division: division,
            }).subscribe(
                (newDivision: M.Division) => {
                    division["@path"] = customOffer.Divisions["@path"].concat([
                        null,
                    ]);
                    this.reloadDivisionFromStore(division).subscribe(
                        (preDivision: M.Division) => {
                            newDivision["@path"] = preDivision["@path"];
                            this.store.dispatch(new UpdateAction(newDivision));
                        },
                    );
                },
                () => {
                    division["@path"] = customOffer.Divisions["@path"].concat([
                        null,
                    ]);
                    this.reloadDivisionFromStore(division).subscribe(
                        (preDivision: M.Division) => {
                            this.store.dispatch(new DeleteAction(preDivision));
                            this.notificationService.error(
                                "Produkt einfügen",
                                `Das Produkt "${preDivision.Label}" konnte nicht eingefügt werden.`,
                            );
                        },
                    );
                },
                () => this.finishedPending(),
            );
        } else {
            this.boundAction("addManualDivision", {
                division: division,
            }).subscribe(
                (newDivision) => {
                    if (customOffer) {
                        this.store.dispatch(
                            new AddAction(customOffer.Divisions, newDivision),
                        );
                    } else {
                        this.reload();
                    }
                },
                undefined,
                () => this.finishedPending(),
            );
        }
    }

    public deleteManualDivision(division: M.Division) {
        this.addPending();
        division.$remove = true;
        this.store.dispatch(new UpdateAction(division));
        this.deleteChain = this.deleteChain
            .then(
                () =>
                    new Promise((resolve) => {
                        this.updateCache.remove(
                            "Divisions",
                            division,
                            undefined,
                            () => {
                                this.reload();
                                this.logger.log(
                                    "failed to delete %d",
                                    division.Id,
                                );
                            },
                            () => {
                                this.finishedPending();
                                resolve(null);
                            },
                            (entity) => {
                                this.reloadDivisionFromStore(entity).subscribe(
                                    (postDivision: M.Division) => {
                                        delete division.$remove;
                                        this.store.dispatch(
                                            new DeleteAction(division),
                                        );
                                    },
                                );
                            },
                        );
                    }),
            )
            .catch();
    }

    public addAttachment(
        collection: string,
        entity: any,
        property: string,
        attachment: Attachment,
        content: File,
    ) {
        if (entity === undefined || entity === null) {
            assert(false, "Entity cannot be added:", entity);
            return;
        }

        const url = `${this.serviceUrl}/${collection}(${entity.Id})/${property}`;
        this.http
            .post<Attachment>(url, attachment, this.reqOpts)
            .subscribe((newPartialValue) => {
                const uploadUrl = `${this.serviceUrl}/Attachments(${newPartialValue.Id})/$value`;
                const validateUrl = `${this.serviceUrl}/Attachments(${newPartialValue.Id})/com.zurich.zbd.data.model.Attachment.deleteIfInvalidPdf`;
                const headers = new HttpHeaders()
                    .append(
                        "Content-Type",
                        content.type ? content.type : "application/binary",
                    )
                    .append("Accept", "application/json");
                const reqOpts = {
                    headers: headers,
                };
                this.http.put(uploadUrl, content, reqOpts).subscribe(
                    () => {
                        this.http
                            .post<{
                                value: boolean;
                            }>(validateUrl, {}, this.reqOpts)
                            .subscribe(
                                (deleted) => {
                                    if (deleted.value) {
                                        this.notificationService.error(
                                            "Anhang hinzufügen",
                                            `In der von Ihnen hochgeladenen Datei "${attachment.Name}" ist in den "Dokumenteneigenschaften  - Sicherheit" der Eintrag "Sicherheitssystem" auf einen anderen Wert als "Keine Sicherheit" eingestellt. Die Datei kann aufgrund dieser Einstellungen nicht an die Beratungsdokumentation angehangen werden.`,
                                        );
                                        // let list: any[] = entity[property]
                                        // Add partial value to store so that attachment component can
                                        // delete it again.
                                        // this.store.dispatch(new AddAction(list, newPartialValue))
                                    } else {
                                        this.getAttachment(
                                            newPartialValue.Id,
                                        ).subscribe((newValue) => {
                                            const list: any[] =
                                                entity[property];
                                            this.store.dispatch(
                                                new AddAction(list, newValue),
                                            );
                                            this.logger.log(
                                                "Attachment %s uploaded",
                                                newValue.Name,
                                            );
                                        });
                                    }
                                },
                                (error) => {
                                    this.notificationService.errorExt(
                                        "Anhang hinzufügen",
                                        "Bei der Überprüfung des Anhangs ist ein Fehler aufgetreten.",
                                        error,
                                    );
                                    this.getAttachment(
                                        newPartialValue.Id,
                                    ).subscribe((newValue) => {
                                        const list: any[] = entity[property];
                                        this.store.dispatch(
                                            new AddAction(list, newValue),
                                        );
                                        this.logger.log(
                                            "Attachment %s uploaded",
                                            newValue.Name,
                                        );
                                    });
                                },
                            );
                    },
                    (error) => {
                        this.notificationService.errorExt(
                            "Anhang hinzufügen",
                            "Anhang konnte nicht hinzugefügt werden.",
                            error,
                        );
                        const list: any[] = entity[property];
                        // Add partial value to store so that attachment component can
                        // delete it again.
                        this.store.dispatch(
                            new AddAction(list, newPartialValue),
                        );
                    },
                );
            });
    }
    protected getAttachment(id: number): Observable<Attachment> {
        const url = `${this.serviceUrl}/Attachments(${id})?$expand=*($levels=20)`;
        return this.http.get<Attachment>(url, this.reqOpts);
    }

    private textCache = new Map<string, Observable<M.ResolvedText>>();
    public textsForTarget(
        target: string,
        division: M.Division,
        retries = 5,
    ): Observable<M.ResolvedText> {
        const divisionId = division ? division.Id : null;
        const cacheKey = `${target}:${divisionId}`;

        if (!this.textCache.has(cacheKey)) {
            const subject = new ReplaySubject<M.ResolvedText>();

            this.boundAction("textsForTarget", {
                target: target,
                divisionId: divisionId,
            })
                .pipe(first())
                .subscribe(subject);

            this.textCache.set(cacheKey, subject);

            subject.pipe(
                catchError((e) => {
                    if (retries == 0) return;
                    this.textCache.delete(cacheKey);
                    return this.textsForTarget(target, division, retries - 1);
                }),
            );
        }

        return this.textCache.get(cacheKey);
    }
}

class LocalState {
    cp: M.ConsultationProcess;

    static create(store: Store<RootState>): Observable<LocalState> {
        return stateBuilder(store, new LocalState()).apply(
            "cp",
            (s) => s.model.ConsultationProcess,
        ).state$;
    }
}
